import { apiClient } from './apiClient';
import { UserProfile } from '../types/user';
import {
  externalApiClientCreatedSchema,
  externalApiClientMetadataSchema,
  termsAcceptanceResponseSchema,
  userProfileResponseSchema,
  type ExternalApiClientCreated,
  type ExternalApiClientMetadata,
} from '../schemas/user';
import { profileLogger } from '../utils/loggerInstance';
import axios from 'axios';

export type { ExternalApiClientCreated, ExternalApiClientMetadata } from '../schemas/user';

export type ExternalApiClientRotateOptions = {
  expires_in_days?: number;
  expires_at?: string;
};

function formatZodIssues(error: { issues: { path: PropertyKey[]; message: string }[] }): string {
  return error.issues.map((i) => `${i.path.map(String).join('.')}: ${i.message}`).join('; ');
}

/**
 * Service for user-related API interactions
 */
export const userService = {
  /**
   * Get the current user's profile.
   * Validates the API response with a Zod schema so last_model, last_industry,
   * and other fields stay in sync with the API contract.
   */
  getProfile: async (): Promise<UserProfile> => {
    profileLogger.info('[getProfile] Requesting /api/v1/users/profile');
    try {
      const response = await apiClient.get('/api/v1/users/profile');
      profileLogger.debug('[getProfile] Response received', {
        status: response.status,
        hasData: !!response.data,
      });
      const parsed = userProfileResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        const message = parsed.error.issues
          .map((i) => `${i.path.join('.')}: ${i.message}`)
          .join('; ');
        profileLogger.error('[getProfile] Response validation failed', {
          message,
          issues: parsed.error.issues,
          rawDataKeys: response.data ? Object.keys(response.data) : [],
        });
        throw new Error(`Profile response validation failed: ${message}`);
      }
      profileLogger.info('[getProfile] Success');
      const data = parsed.data;
      const profile: UserProfile = {
        id: data.id,
        user_id: data.user_id,
        first_name: data.first_name ?? null,
        last_name: data.last_name ?? null,
        company_name: data.company_name ?? null,
        position_title: data.position_title ?? null,
        email: data.email,
        stripe_customer_id: data.stripe_customer_id ?? null,
        organization_role: data.organization_role ?? null,
        profile_photo_url: data.profile_photo_url ?? null,
        has_photo_data: data.has_photo_data ?? false,
        last_model: data.last_model ?? null,
        last_industry: data.last_industry ?? null,
        created_at: data.created_at.toISOString(),
        updated_at: data.updated_at?.toISOString() ?? null,
      };
      return profile;
    } catch (err: unknown) {
      const axiosErr = err && typeof err === 'object' && 'isAxiosError' in err ? err : null;
      profileLogger.error('[getProfile] Failed', {
        message: err instanceof Error ? err.message : String(err),
        status:
          axiosErr && 'response' in axiosErr
            ? (axiosErr as { response?: { status?: number } }).response?.status
            : undefined,
        responseData:
          axiosErr && 'response' in axiosErr
            ? (axiosErr as { response?: { data?: unknown } }).response?.data
            : undefined,
      });
      throw err;
    }
  },

  /**
   * Get the current user's profile photo as a blob
   */
  getProfilePhoto: async (): Promise<Blob> => {
    const response = await apiClient.get('/api/v1/users/profile/photo', {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Update the current user's profile
   */
  updateProfile: async (data: Partial<UserProfile>): Promise<UserProfile> => {
    const response = await apiClient.patch('/api/v1/users/profile', data);
    return response.data;
  },

  /**
   * Upload a new profile photo
   */
  uploadProfilePhoto: async (file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);

    await apiClient.post('/api/v1/users/profile/photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Delete the current profile photo
   */
  deleteProfilePhoto: async (): Promise<void> => {
    await apiClient.delete('/api/v1/users/profile/photo');
  },

  /**
   * Get the current user's post-onboarding tutorial status
   */
  getTutorialStatus: async (): Promise<{
    tutorial_completed: boolean;
    tutorial_completed_at: string | null;
  }> => {
    const response = await apiClient.get<{
      tutorial_completed: boolean;
      tutorial_completed_at: string | null;
    }>('/api/v1/users/tutorial-status');
    return response.data;
  },

  /**
   * Mark the post-onboarding tutorial as completed (or skipped)
   */
  markTutorialComplete: async (): Promise<void> => {
    await apiClient.post('/api/v1/users/tutorial-complete');
  },

  /**
   * Get the current user's terms acceptance status (server-side).
   */
  getTermsAcceptance: async (): Promise<{
    terms_accepted: boolean;
    terms_accepted_at: string | null;
    /** Present when the API returns it; used to pre-fill AcceptTerms without a second guess. */
    marketing_emails_opt_in?: boolean;
  }> => {
    const response = await apiClient.get('/api/v1/users/terms-acceptance');
    const parsed = termsAcceptanceResponseSchema.safeParse(
      response.data ?? { terms_accepted: false, terms_accepted_at: null },
    );
    if (!parsed.success) {
      const message = parsed.error.issues
        .map((i) => `${i.path.join('.')}: ${i.message}`)
        .join('; ');
      profileLogger.error('[getTermsAcceptance] Response validation failed', {
        message,
        issues: parsed.error.issues,
        rawDataKeys:
          response.data && typeof response.data === 'object' ? Object.keys(response.data) : [],
      });
      throw new Error(`Terms acceptance response validation failed: ${message}`);
    }
    const data = parsed.data;
    return {
      terms_accepted: data.terms_accepted,
      terms_accepted_at: data.terms_accepted_at ?? null,
      ...(typeof data.marketing_emails_opt_in === 'boolean'
        ? { marketing_emails_opt_in: data.marketing_emails_opt_in }
        : {}),
    };
  },

  /**
   * Record the current user's acceptance of Terms and Privacy Policy (e.g. post sign-up).
   * Optionally records optional marketing/tips email consent in the same request.
   */
  acceptTerms: async (options?: { marketing_emails_opt_in: boolean }): Promise<void> => {
    await apiClient.post('/api/v1/users/accept-terms', options ?? {});
  },

  /**
   * Permanently close the account (soft delete in API + Firebase when configured).
   * Requires CSRF (sent automatically by apiClient for DELETE).
   */
  deleteAccount: async (): Promise<void> => {
    await apiClient.delete('/api/v1/users/me');
  },

  /**
   * External API credential metadata (no client_secret; shown only on generate/rotate).
   * Returns null when the user has no active credentials (404).
   */
  getExternalApiClient: async (): Promise<ExternalApiClientMetadata | null> => {
    try {
      const response = await apiClient.get('/api/v1/users/me/external-api-client');
      const parsed = externalApiClientMetadataSchema.safeParse(response.data);
      if (!parsed.success) {
        const message = formatZodIssues(parsed.error);
        profileLogger.error('[getExternalApiClient] Response validation failed', {
          message,
          issues: parsed.error.issues,
        });
        throw new Error(`External API client response validation failed: ${message}`);
      }
      return parsed.data;
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  },

  /**
   * Generate or regenerate client_id + client_secret with an expiry.
   * Plaintext client_secret is only returned in this response; save it.
   */
  rotateExternalApiClient: async (
    options?: ExternalApiClientRotateOptions,
  ): Promise<ExternalApiClientCreated> => {
    const response = await apiClient.post('/api/v1/users/me/external-api-client/rotate', {
      expires_in_days: options?.expires_in_days ?? 90,
      ...(options?.expires_at ? { expires_at: options.expires_at } : {}),
    });
    const parsed = externalApiClientCreatedSchema.safeParse(response.data);
    if (!parsed.success) {
      const message = formatZodIssues(parsed.error);
      profileLogger.error('[rotateExternalApiClient] Response validation failed', {
        message,
        issues: parsed.error.issues,
      });
      throw new Error(`External API client rotate response validation failed: ${message}`);
    }
    return parsed.data;
  },
};
